// link-validation.ts
import { AppService } from '../../app.service';
import { UtilityFunctions } from '../../shared/utility-functions';
import { TimeCurrencyService } from './../time-currency.service';
import { LinkFacility } from './link-facility.interface';
import { CONSTANT_VALUES } from '../../constants/constant-values';


/**
 * Validation maps used by LinkComponent and LinkValidation.
 * Properties are optional so LinkComponent can assign {} without compile errors.
 */
export interface ValidationMap {
  validLinkType?: any;
  validName?: any;
  validDate?: any;
  validIcon?: any;
  validDescription?: any;
  validExpiryDate?: any;
  validCtaText?: any;
  validCtaLink?: any;
  validAddressLine1?: any;
  validPincode?: any;
  validCountry?: any;
  validOpenTime_hour?: any;
  validOpenTime_minute?: any;
  validCloseTime_hour?: any;
  validCloseTime_minute?: any;
  validMaxNumberOfPeople?: any;
  validmaxNumberOfPeoplePerSlot?: any;
  validWaitlistCount?: any;
  GSTValue?: any;
  validReminder?: any;
  holdBookingDays?: any;
  validNotificationAcceptanceHour?: any;
  validMemberAmount?: any;
  validNonMemberAmount?: any;
  validGuestAmount?: any;
  validCorpMemberAmount?: any;
  validCorpNonMemberAmount?: any;
  validMemberChildAmount?: any;
  validNonMemberChildAmount?: any;
  validGuestChildAmount?: any;
  validCorpMemberChildAmount?: any;
  validCorpNonMemberChildAmount?: any;
  [key: string]: any;
}

export interface ErrorMessageMap {
  linkTypeError?: string;
  nameError?: string;
  dateError?: string;
  iconError?: string;
  descriptionError?: string;
  expiryDateError?: string;
  ctaTextError?: string;
  ctaLinkError?: string;
  addressLine1Error?: string;
  pincodeError?: string;
  countryError?: string;
  timingError?: string;
  capacityError?: string;
  waitlistError?: string;
  gstError?: string;
  reminderError?: string;
  notificationError?: string;
  memberAmountError?: string;
  nonMemberAmountError?: string;
  guestAmountError?: string;
  corpMemberAmountError?: string;
  corpNonMemberAmountError?: string;
  memberChildAmountError?: string;
  nonMemberChildAmountError?: string;
  guestChildAmountError?: string;
  corpMemberChildAmountError?: string;
  corpNonMemberChildAmountError?: string;
  [key: string]: string | undefined;
}

/**
 * LinkValidation class used by LinkComponent to validate link objects.
 * It intentionally uses permissive types for backward compatibility (Angular 2 / older TS).
 */
export class LinkValidation {
  public validate: ValidationMap = {};
  public errorMessages: ErrorMessageMap = {};

  constructor(
    private appService: AppService,
    private utilityFunctions: UtilityFunctions,
    private timeCurrencyService: TimeCurrencyService
  ) {
    this.initializeValidation();
  }

  private initializeValidation(): void {
    // initialize validate map with keys (optional) but empty values
    this.validate = {
      validLinkType: '',
      validName: '',
      validDate: '',
      validIcon: '',
      validDescription: '',
      validExpiryDate: '',
      validCtaText: '',
      validCtaLink: '',
      validAddressLine1: '',
      validPincode: '',
      validCountry: '',
      validOpenTime_hour: '',
      validOpenTime_minute: '',
      validCloseTime_hour: '',
      validCloseTime_minute: '',
      validMaxNumberOfPeople: '',
      validmaxNumberOfPeoplePerSlot: '',
      validWaitlistCount: '',
      GSTValue: '',
      validReminder: '',
      holdBookingDays: '',
      validNotificationAcceptanceHour: '',
      validMemberAmount: '',
      validNonMemberAmount: '',
      validGuestAmount: '',
      validCorpMemberAmount: '',
      validCorpNonMemberAmount: '',
      validMemberChildAmount: '',
      validNonMemberChildAmount: '',
      validGuestChildAmount: '',
      validCorpMemberChildAmount: '',
      validCorpNonMemberChildAmount: ''
    };

    // initialize corresponding error message placeholders
    var keys = Object.keys(this.validate);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i] + 'Error';
      this.errorMessages[k] = '';
    }
    // also add specific names used elsewhere
    this.errorMessages['expiryDateError'] = '';
    this.errorMessages['providerError'] = '';
  }

  /**
   * Main validation entry for a LinkComponent instance.
   * Returns true if valid, false if not.
   */
  validateFacilityObj(component: any): boolean {
    // clear old error states
    this.clearAllValidationErrors();

    var isValid = true;

    if (!this.validateLinkBasics(component)) isValid = false;
    if (!this.validateLinkSpecificFields(component)) isValid = false;
    if (!this.validateAddressFields(component)) isValid = false;
    if (!this.validateLinkTimings(component)) isValid = false;
    if (!this.validateLinkCapacity(component)) isValid = false;
    if (!this.validateAttendeeManagement(component)) isValid = false;
    if (!this.validateWaitlist(component)) isValid = false;
    if (!this.validateGST(component)) isValid = false;
    if (!this.validateNotificationSettings(component)) isValid = false;
    if (!this.validatePricing(component)) isValid = false;

    return isValid;
  }

  /* ---------------------------
     Helper utilities
     ---------------------------*/
  private safeString(value: any): string {
    if (value === null || value === undefined) return '';
    return ('' + value).trim();
  }

  private isValidDate(d: any): boolean {
    if (!d) return false;
    var dt = (d instanceof Date) ? d : new Date(d);
    return !isNaN(dt.getTime());
  }

  private todayZero(): Date {
    var t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }

  private setFieldRequired(field: string, message?: string): void {
    this.validate[field] = 'required';
    if (message) {
      // map field to errorMessages key pattern ...Error
      var key = field + 'Error';
      this.errorMessages[key] = message;
    }
  }

  private setFieldSuccess(field: string): void {
    this.validate[field] = '';
    var key = field + 'Error';
    this.errorMessages[key] = '';
  }

  /* ---------------------------
     Basic validation group
     ---------------------------*/
  private validateLinkBasics(component: any): boolean {
    var ok = true;
    var fac = component && component.facility ? component.facility : null;

    // linkType
    if (!fac || this.safeString(fac.linkType) === '') {
      this.setFieldRequired('validLinkType', 'Link type is required');
      ok = false;
    } else {
      this.setFieldSuccess('validLinkType');
    }

    // name
    if (!fac || this.safeString(fac.name).length < 3) {
      this.setFieldRequired('validName', 'Name must be at least 3 characters');
      ok = false;
    } else {
      this.setFieldSuccess('validName');
    }

    // date
    // date
    if (!fac || !this.isValidDate(fac.date)) {
      this.setFieldRequired('validDate', 'Date is required and must be valid');
      ok = false;
    } else {
      var sel = new Date(fac.date);
      sel.setHours(0, 0, 0, 0);

      // If component indicates we're creating a new link, disallow past dates.
      // If editing/viewing an existing link, allow past dates.
      var enforceNotPast = !!(component && component.addLinkStatus);

      if (enforceNotPast) {
        if (sel < this.todayZero()) {
          this.setFieldRequired('validDate', 'Date cannot be in the past');
          ok = false;
        } else {
          this.setFieldSuccess('validDate');
        }
      } else {
        // editing/view mode — accept the date even if in the past
        this.setFieldSuccess('validDate');
      }
    }


    // icon
    if (!fac || this.safeString(fac.icon) === '') {
      this.setFieldRequired('validIcon', 'Icon is required');
      ok = false;
    } else {
      this.setFieldSuccess('validIcon');
    }

    return ok;
  }

  /* ---------------------------
     Link specific (expiry, CTA)
     ---------------------------*/
  private validateLinkSpecificFields(component: any): boolean {
    var ok = true;
    var fac = component && component.facility ? component.facility : null;

    // expiry date required and must be in future and not too far (2 years)
    // expiry date required and must be in future and not too far (2 years)
    // Always require an expiry date (exists & parseable)
    if (!fac || !this.isValidDate(fac.expiryDate)) {
      this.setFieldRequired('validExpiryDate', 'Expiry date is required');
      ok = false;
    } else {
      var exp = new Date(fac.expiryDate);
      exp.setHours(0, 0, 0, 0);
      var today = this.todayZero();
      var maxFuture = new Date(); maxFuture.setFullYear(maxFuture.getFullYear() + 2); maxFuture.setHours(23, 59, 59, 999);

      var enforceExpiryFuture = !!(component && component.addLinkStatus);

      if (enforceExpiryFuture) {
        if (exp <= today) {
          this.setFieldRequired('validExpiryDate', 'Expiry date must be in the future');
          ok = false;
        } else if (exp > maxFuture) {
          this.setFieldRequired('validExpiryDate', 'Expiry date cannot be more than 2 years in the future');
          ok = false;
        } else {
          this.setFieldSuccess('validExpiryDate');
        }
      } else {
        // editing/view mode: accept any valid expiry date (past or future) but still enforce maxFuture if you want:
        if (exp > maxFuture) {
          this.setFieldRequired('validExpiryDate', 'Expiry date cannot be more than 2 years in the future');
          ok = false;
        } else {
          this.setFieldSuccess('validExpiryDate');
        }
      }
    }


    // CTA
    var ctaEnabled = !!(fac && fac.ctaEnabled);
    if (ctaEnabled) {
      if (!this.safeString(fac.ctaText)) {
        this.setFieldRequired('validCtaText', 'CTA text is required when CTA enabled');
        ok = false;
      } else {
        this.setFieldSuccess('validCtaText');
      }

      if (!this.safeString(fac.ctaLink)) {
        this.setFieldRequired('validCtaLink', 'CTA link is required when CTA enabled');
        ok = false;
      } else if (!this.validateURL(fac.ctaLink)) {
        this.setFieldRequired('validCtaLink', 'CTA link must be a valid URL');
        ok = false;
      } else {
        this.setFieldSuccess('validCtaLink');
      }
    } else {
      this.setFieldSuccess('validCtaText');
      this.setFieldSuccess('validCtaLink');
    }

    return ok;
  }

  /* ---------------------------
     URL validation (try URL constructor then fallback)
     ---------------------------*/
  private validateURL(url: string): boolean {
    if (!url) return false;
    var candidate = this.safeString(url);
    try {
      // attempt to use standard URL parsing; if no protocol provided, try https
      if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(candidate)) {
        new (window as any).URL('https://' + candidate);
        return true;
      }
      new (window as any).URL(candidate);
      return true;
    } catch (e) {
      // fallback permissive regex
      var regex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d+)?(\/[^\s]*)?$/i;
      return regex.test(candidate);
    }
  }

  /* ---------------------------
     Address & pincode validation
     ---------------------------*/
  private validateAddressFields(component: any): boolean {
    var ok = true;
    var fac = component && component.facility ? component.facility : null;

    if (fac && fac.addressLine1 && this.safeString(fac.addressLine1).length < 5) {
      this.setFieldRequired('validAddressLine1', 'Address must be at least 5 characters if provided');
      ok = false;
    } else {
      this.setFieldSuccess('validAddressLine1');
    }

    if (fac && fac.pincode) {
      if (!this.validatePincodeWithCountry(fac.pincode, component)) {
        ok = false;
      } else {
        this.setFieldSuccess('validPincode');
      }
    } else {
      this.setFieldSuccess('validPincode');
    }

    if (fac && fac.country !== undefined && this.safeString(fac.country) === '') {
      this.setFieldRequired('validCountry', 'Country cannot be empty');
      ok = false;
    } else {
      this.setFieldSuccess('validCountry');
    }

    return ok;
  }

  private validatePincodeWithCountry(pincode: string, component: any): boolean {
    var country = this.getCurrentCountryFromComponent(component);
    if (!this.timeCurrencyService || typeof this.timeCurrencyService.isPostalCodeRequired !== 'function') {
      // fallback simple check
      if (!pincode || this.safeString(pincode).length < 3) {
        this.setFieldRequired('validPincode', 'Pincode is required and must be valid');
        return false;
      }
      this.setFieldSuccess('validPincode');
      return true;
    }

    if (!this.timeCurrencyService.isPostalCodeRequired(country)) {
      this.setFieldSuccess('validPincode');
      return true;
    }

    if (!pincode || this.safeString(pincode) === '') {
      this.setFieldRequired('validPincode', 'Pincode is required for selected country');
      return false;
    }

    if (!this.timeCurrencyService.validatePincodeForCountry(pincode.trim(), country)) {
      this.setFieldRequired('validPincode', 'Invalid pincode for selected country');
      return false;
    }

    this.setFieldSuccess('validPincode');
    return true;
  }

  private getCurrentCountryFromComponent(component: any): string {
    if (component && component.facility && this.safeString(component.facility.country)) {
      return component.facility.country.trim();
    }
    if (component && component.vendor && this.safeString(component.vendor.country)) {
      return component.vendor.country.trim();
    }
    return 'Singapore';
  }

  /* ---------------------------
     Timings / capacity / attendees / waitlist / GST / notification
     ---------------------------*/
  private validateLinkTimings(component: any): boolean {
    var ok = true;
    // treat missing values as required (component may store them as openTime_hour etc)
    var props = ['openTime_hour', 'openTime_minute', 'closeTime_hour', 'closeTime_minute'];
    var keys = ['validOpenTime_hour', 'validOpenTime_minute', 'validCloseTime_hour', 'validCloseTime_minute'];
    for (var i = 0; i < props.length; i++) {
      var val = (component as any)[props[i]];
      if (val === undefined || val === null || this.safeString(val) === '') {
        this.setFieldRequired(keys[i], 'Time value required');
        ok = false;
      } else {
        this.setFieldSuccess(keys[i]);
      }
    }
    return ok;
  }

  private validateLinkCapacity(component: any): boolean {
    var fac = component && component.facility ? component.facility : null;
    var raw = fac ? this.safeString(fac.maxBookingsPerSlot) : '';
    if (!raw) {
      this.setFieldSuccess('validmaxNumberOfPeoplePerSlot');
      return true;
    }
    if (raw.toLowerCase && raw.toLowerCase() === 'unlimited') {
      this.setFieldSuccess('validmaxNumberOfPeoplePerSlot');
      return true;
    }
    var n = parseInt(raw, 10);
    if (!isFinite(n) || n <= 0) {
      this.setFieldRequired('validmaxNumberOfPeoplePerSlot', 'Capacity must be positive number or "unlimited"');
      return false;
    }
    this.setFieldSuccess('validmaxNumberOfPeoplePerSlot');
    return true;
  }

  private validateAttendeeManagement(component: any): boolean {
    var fac = component && component.facility ? component.facility : null;
    if (!fac || !fac.isAttendeeCountRequired) {
      this.setFieldSuccess('validMaxNumberOfPeople');
      return true;
    }
    var raw = this.safeString(fac.maxNumberOfPeople);
    if (!raw) {
      this.setFieldRequired('validMaxNumberOfPeople', 'Max attendees required');
      return false;
    }
    var n = parseInt(raw, 10);
    if (!isFinite(n) || n <= 0) {
      this.setFieldRequired('validMaxNumberOfPeople', 'Max attendees must be positive number');
      return false;
    }
    this.setFieldSuccess('validMaxNumberOfPeople');
    return true;
  }

  private validateWaitlist(component: any): boolean {
    var fac = component && component.facility ? component.facility : null;
    if (!fac || !fac.isWaitlistEnabled) {
      this.setFieldSuccess('validWaitlistCount');
      return true;
    }
    var raw = this.safeString(fac.waitlistCount);
    if (!raw) {
      this.setFieldRequired('validWaitlistCount', 'Waitlist count required');
      return false;
    }
    var n = parseInt(raw, 10);
    if (!isFinite(n) || n <= 0 || n > 3) {
      this.setFieldRequired('validWaitlistCount', 'Waitlist must be between 1 and 3');
      return false;
    }
    this.setFieldSuccess('validWaitlistCount');
    return true;
  }

  private validateGST(component: any): boolean {
    var fac = component && component.facility ? component.facility : null;
    if (!fac || !fac.isGSTApplicable) {
      this.setFieldSuccess('GSTValue');
      return true;
    }
    var raw = this.safeString(fac.GSTValue);
    if (!raw) {
      this.setFieldRequired('GSTValue', 'GST value required when enabled');
      return false;
    }
    var n = parseFloat(raw);
    if (!isFinite(n) || n < 0 || n > 100) {
      this.setFieldRequired('GSTValue', 'GST must be between 0 and 100');
      return false;
    }
    if (raw.indexOf('.') >= 0 && raw.split('.')[1].length > 2) {
      this.setFieldRequired('GSTValue', 'GST may have at most 2 decimal places');
      return false;
    }
    this.setFieldSuccess('GSTValue');
    return true;
  }

  private validateNotificationSettings(component: any): boolean {
    var fac = component && component.facility ? component.facility : null;
    if (!fac || !fac.reminder || this.safeString(fac.reminder.value) === '') {
      this.setFieldRequired('validReminder', 'Reminder value required');
      return false;
    } else {
      this.setFieldSuccess('validReminder');
    }
    if (fac && (fac.notificationAcceptanceHour === undefined || this.safeString(fac.notificationAcceptanceHour) === '')) {
      this.setFieldRequired('validNotificationAcceptanceHour', 'Notification acceptance hour required');
      return false;
    } else {
      this.setFieldSuccess('validNotificationAcceptanceHour');
    }
    return true;
  }

  /* ---------------------------
     Pricing validation (auto-disable zeros)
     ---------------------------*/
  private validatePricing(component: any): boolean {
    var ok = true;
    if (!component || !component.facility || !component.facility.activityCharges) return true;

    var tiersDef: any[] = [
      { key: 'member', validation: 'validMemberAmount', childKey: 'childAmount', childValidation: 'validMemberChildAmount' },
      { key: 'nonMember', validation: 'validNonMemberAmount', childKey: 'childAmount', childValidation: 'validNonMemberChildAmount' },
      { key: 'guest', validation: 'validGuestAmount', childKey: 'childAmount', childValidation: 'validGuestChildAmount' },
      { key: 'corpMember', validation: 'validCorpMemberAmount' },
      { key: 'corpNonMember', validation: 'validCorpNonMemberAmount' }
    ];

    for (var i = 0; i < tiersDef.length; i++) {
      var t = tiersDef[i];
      var pTier = (component.facility.activityCharges as any)[t.key];

      if (!pTier || !pTier.enabled) {
        this.setFieldSuccess(t.validation);
        if (t.childValidation) this.setFieldSuccess(t.childValidation);
        continue;
      }

      var amtStr = this.safeString(pTier.amount);
      var amtNum = parseFloat(amtStr || '0');

      if (amtNum === 0) {
        // auto-disable pricing and update UI via component hooks if provided
        pTier.enabled = false;
        if (typeof component.updateToggleUI === 'function') component.updateToggleUI(t.key, false);
        this.setFieldSuccess(t.validation);
        if (t.childValidation) this.setFieldSuccess(t.childValidation);
        continue;
      }

      if (!amtStr) {
        this.setFieldRequired(t.validation, t.key + ' amount is required');
        ok = false;
      } else if (!isFinite(amtNum) || amtNum < 0) {
        this.setFieldRequired(t.validation, t.key + ' amount must be a valid positive number');
        ok = false;
      } else {
        this.setFieldSuccess(t.validation);
      }

      // child
      if (t.childValidation && pTier.childEnabled) {
        var childStr = this.safeString(pTier.childAmount);
        var childNum = parseFloat(childStr || '0');

        if (childNum === 0) {
          pTier.childEnabled = false;
          if (typeof component.updateChildToggleUI === 'function') component.updateChildToggleUI(t.key, false);
          this.setFieldSuccess(t.childValidation);
        } else if (!childStr) {
          this.setFieldRequired(t.childValidation, t.key + ' child amount is required');
          ok = false;
        } else if (!isFinite(childNum) || childNum < 0) {
          this.setFieldRequired(t.childValidation, t.key + ' child amount must be a valid positive number');
          ok = false;
        } else {
          this.setFieldSuccess(t.childValidation);
        }
      } else if (t.childValidation) {
        this.setFieldSuccess(t.childValidation);
      }
    }

    return ok;
  }

  /* ---------------------------
     Single-field validation API (keeps original mapping)
     ---------------------------*/
  validateFacility(component: any, fieldName: string, value: any): boolean {
    switch (fieldName) {
      case 'facility.linkType': return this.validateLinkTypeValue(value);
      case 'facility.name': return this.validateLinkNameValue(value);
      case 'facility.date': return this.validateLinkDateValue(value);
      case 'facility.description': return this.validateDescriptionValue(value);
      case 'facility.expiryDate': return this.validateExpiryDateValue(value);
      case 'facility.ctaText': return this.validateCtaTextValue(value);
      case 'facility.ctaLink': return this.validateCtaLinkValue(value);
      case 'facility.addressLine1': return this.validateAddressLine1Value(value);
      case 'facility.pincode': return this.validatePincodeWithCountry(value, component);
      case 'facility.country': return this.validateCountryValue(value);
      case 'facility.maxBookingsPerSlot': return this.validateLinkCapacityValue(value);
      case 'facility.waitlistCount': return this.validateWaitlistCountValue(value);
      case 'facility.GSTValue': return this.validateGSTValueValue(value);
      case 'icon': return this.validateIconValue(value);
      case 'facility.activityCharges.member.childAmount': return this.validateMemberChildAmountValue(value);
      case 'facility.activityCharges.nonMember.childAmount': return this.validateNonMemberChildAmountValue(value);
      case 'facility.activityCharges.guest.childAmount': return this.validateGuestChildAmountValue(value);
      case 'facility.activityCharges.corpMember.childAmount': return this.validateCorpMemberChildAmountValue(value);
      case 'facility.activityCharges.corpNonMember.childAmount': return this.validateCorpNonMemberChildAmountValue(value);
      default: return true;
    }
  }

  /* ---------------------------
     Individual validators (thin wrappers)
     ---------------------------*/
  private validateLinkTypeValue(value: any): boolean {
    if (!this.safeString(value)) { this.setFieldRequired('validLinkType', 'Link type required'); return false; }
    this.setFieldSuccess('validLinkType'); return true;
  }

  private validateLinkNameValue(value: any): boolean {
    if (!this.safeString(value) || this.safeString(value).length < 3) { this.setFieldRequired('validName', 'Name must be at least 3 chars'); return false; }
    this.setFieldSuccess('validName'); return true;
  }

  private validateLinkDateValue(value: any): boolean {
    if (!this.isValidDate(value)) { this.setFieldRequired('validDate', 'Invalid date'); return false; }
    var sel = new Date(value); sel.setHours(0, 0, 0, 0);
    if (sel < this.todayZero()) { this.setFieldRequired('validDate', 'Date cannot be in past'); return false; }
    this.setFieldSuccess('validDate'); return true;
  }

  private validateDescriptionValue(value: any): boolean {
    if (!this.safeString(value) || this.safeString(value).length < 5) { this.setFieldRequired('validDescription', 'Description must be at least 5 chars'); return false; }
    this.setFieldSuccess('validDescription'); return true;
  }

  private validateExpiryDateValue(value: any): boolean {
    if (!this.isValidDate(value)) { this.setFieldRequired('validExpiryDate', 'Invalid expiry date'); return false; }
    var exp = new Date(value); exp.setHours(0, 0, 0, 0);
    if (exp <= this.todayZero()) { this.setFieldRequired('validExpiryDate', 'Expiry date must be in future'); return false; }
    this.setFieldSuccess('validExpiryDate'); return true;
  }

  private validateCtaTextValue(value: any): boolean {
    if (!this.safeString(value)) { this.setFieldRequired('validCtaText', 'CTA text required'); return false; }
    this.setFieldSuccess('validCtaText'); return true;
  }

  private validateCtaLinkValue(value: any): boolean {
    if (!this.safeString(value) || !this.validateURL(value)) { this.setFieldRequired('validCtaLink', 'Invalid CTA link'); return false; }
    this.setFieldSuccess('validCtaLink'); return true;
  }

  private validateAddressLine1Value(value: any): boolean {
    if (value && this.safeString(value).length < 5) { this.setFieldRequired('validAddressLine1', 'Address must be at least 5 chars'); return false; }
    this.setFieldSuccess('validAddressLine1'); return true;
  }

  private validateCountryValue(value: any): boolean {
    if (value !== undefined && this.safeString(value) === '') { this.setFieldRequired('validCountry', 'Country cannot be empty'); return false; }
    this.setFieldSuccess('validCountry'); return true;
  }

  private validateWaitlistCountValue(value: any): boolean {
    var raw = this.safeString(value);
    if (!raw) { this.setFieldRequired('validWaitlistCount', 'Waitlist count required'); return false; }
    var n = parseInt(raw, 10);
    if (!isFinite(n) || n <= 0 || n > 3) { this.setFieldRequired('validWaitlistCount', 'Waitlist must be between 1 and 3'); return false; }
    this.setFieldSuccess('validWaitlistCount'); return true;
  }

  validateGSTValueValue(value: any): boolean {
    var raw = this.safeString(value);
    if (!raw) return true;
    var n = parseFloat(raw);
    if (!isFinite(n) || n < 0 || n > 100) { this.setFieldRequired('GSTValue', 'GST must be between 0 and 100'); return false; }
    if (raw.indexOf('.') >= 0 && raw.split('.')[1].length > 2) { this.setFieldRequired('GSTValue', 'GST max 2 decimals'); return false; }
    this.setFieldSuccess('GSTValue'); return true;
  }

  private validateIconValue(value: any): boolean {
    if (!value) { this.setFieldRequired('validIcon', 'Icon is required'); return false; }
    this.setFieldSuccess('validIcon'); return true;
  }

  private validateLinkCapacityValue(value: any): boolean {
    var v = this.safeString(value);
    if (!v) { this.setFieldSuccess('validmaxNumberOfPeoplePerSlot'); return true; }
    if (v.toLowerCase && v.toLowerCase() === 'unlimited') { this.setFieldSuccess('validmaxNumberOfPeoplePerSlot'); return true; }
    var n = parseInt(v, 10);
    if (!isFinite(n) || n <= 0) { this.setFieldRequired('validmaxNumberOfPeoplePerSlot', 'Capacity invalid'); return false; }
    this.setFieldSuccess('validmaxNumberOfPeoplePerSlot'); return true;
  }

  private _validateChildAmount(field: string, value: any): boolean {
    var v = this.safeString(value);
    if (!v) { this.setFieldRequired(field, 'Child amount required'); return false; }
    var n = parseFloat(v);
    if (!isFinite(n) || n < 0) { this.setFieldRequired(field, 'Child amount invalid'); return false; }
    this.setFieldSuccess(field);
    return true;
  }

  private validateMemberChildAmountValue(value: any): boolean { return this._validateChildAmount('validMemberChildAmount', value); }
  private validateNonMemberChildAmountValue(value: any): boolean { return this._validateChildAmount('validNonMemberChildAmount', value); }
  private validateGuestChildAmountValue(value: any): boolean { return this._validateChildAmount('validGuestChildAmount', value); }
  private validateCorpMemberChildAmountValue(value: any): boolean { return this._validateChildAmount('validCorpMemberChildAmount', value); }
  private validateCorpNonMemberChildAmountValue(value: any): boolean { return this._validateChildAmount('validCorpNonMemberChildAmount', value); }

  /* ---------------------------
     Utility: set proper values & clear states
     ---------------------------*/
  setProperValuesToFacilityObj(component: any): void {
    if (!component || !component.facility) return;

    component.facility.timings = component.facility.timings || { openTime: '00:00', closeTime: '00:00' };
    component.facility.timings.openTime = '00:00';
    component.facility.timings.closeTime = '00:00';

    if (component.vendor) {
      if (component.vendor.subVendors && component.vendor.subVendors.length > 0) {
        component.facility.vendorId = component.vendor.subVendors[0]._id;
      } else {
        component.facility.vendorId = component.vendor._id;
      }
      component.facility.createdBy = component.vendor.email || '';
    }

    if (typeof component.generateFullLocation === 'function') component.generateFullLocation();

    component.facility.category = 'Links';
    component.facility.isActive = true;
    component.facility.slotDuration = '0';

    if (component.vendor) {
      component.facility.timezoneOffset = component.vendor.timezoneOffset || new Date().getTimezoneOffset();
      component.facility.timezoneUTC = component.vendor.timezoneUTC || '';
      component.facility.timezoneAbbr = component.vendor.timezoneAbbr || '';
    }

    if (typeof component.applyLinkFieldsToFacility === 'function') component.applyLinkFieldsToFacility();
  }

  clearAllValidationErrors(): void {
    var keys = Object.keys(this.validate);
    for (var i = 0; i < keys.length; i++) {
      this.validate[keys[i]] = '';
    }
    var errKeys = Object.keys(this.errorMessages);
    for (var j = 0; j < errKeys.length; j++) {
      this.errorMessages[errKeys[j]] = '';
    }
  }

  hasValidationErrors(): boolean {
    var keys = Object.keys(this.validate);
    for (var i = 0; i < keys.length; i++) {
      if (this.validate[keys[i]] === 'required') return true;
    }
    return false;
  }

  getValidationErrorCount(): number {
    var count = 0;
    var keys = Object.keys(this.validate);
    for (var i = 0; i < keys.length; i++) {
      if (this.validate[keys[i]] === 'required') count++;
    }
    return count;
  }

  /**
   * Validate Digirestro external API configuration
   */
  public validateDigirestroConfig(component: any): boolean {
    if (!component.facility || !component.facility.externalApi || !component.facility.externalApi.digirestro) {
      return false;
    }

    const digirestro = component.facility.externalApi.digirestro;
    let isValid = true;

    // Validate Base URL
    if (!digirestro.baseUrl || digirestro.baseUrl.trim() === '') {
      this.validate['validDigirestroBaseUrl'] = 'required';
      this.errorMessages['validDigirestroBaseUrl'] = 'Please enter Digirestro Base URL';
      isValid = false;
    } else {
      this.validate['validDigirestroBaseUrl'] = 'success';
      this.errorMessages['validDigirestroBaseUrl'] = '';
    }

    // Validate API Key
    if (!digirestro.apiKey || digirestro.apiKey.trim() === '') {
      this.validate['validDigirestroApiKey'] = 'required';
      this.errorMessages['validDigirestroApiKey'] = 'Please enter Digirestro API Key';
      isValid = false;
    } else {
      this.validate['validDigirestroApiKey'] = 'success';
      this.errorMessages['validDigirestroApiKey'] = '';
    }

    // Validate Store ID
    if (!digirestro.storeId || Number(digirestro.storeId) <= 0 || isNaN(Number(digirestro.storeId))) {
      this.validate['validDigirestroStoreId'] = 'required';
      this.errorMessages['validDigirestroStoreId'] = 'Please enter a valid Digirestro Store ID (positive number)';
      isValid = false;
    } else {
      this.validate['validDigirestroStoreId'] = 'success';
      this.errorMessages['validDigirestroStoreId'] = '';
    }

    // Validate Browser URL
    if (!digirestro.browserURL || digirestro.browserURL.trim() === '') {
      this.validate['validDigirestroBrowserURL'] = 'required';
      this.errorMessages['validDigirestroBrowserURL'] = 'Please enter Digirestro Browser URL';
      isValid = false;
    } else {
      this.validate['validDigirestroBrowserURL'] = 'success';
      this.errorMessages['validDigirestroBrowserURL'] = '';
    }

    // Validate Integration ID
    if (!digirestro.integrationId || digirestro.integrationId.toString().trim() === '') {
      this.validate['validDigirestroIntegrationId'] = 'required';
      this.errorMessages['validDigirestroIntegrationId'] = 'Please enter Digirestro Integration ID';
      isValid = false;
    } else {
      this.validate['validDigirestroIntegrationId'] = 'success';
      this.errorMessages['validDigirestroIntegrationId'] = '';
    }

    return isValid;
  }

  /**
   * Enhanced external API validation - add Digirestro check
   */
  public validateExternalApiProvider(component: any): boolean {
    if (!component.facility || !component.facility.externalApi || !component.facility.externalApi.enabled) {
      return true;
    }

    const provider = (component.facility.externalApi.provider || '').toLowerCase();

    if (provider === 'digirestro') {
      return this.validateDigirestroConfig(component);
    }

    // Existing validation for other providers continues...
    return true;
  }
}



// WEBPACK FOOTER //
// ./src/app/facility/link/link-validation.ts